import { useEffect, useState } from 'react';

/**
 * Snapshot do estado de rede do device, lido via Network Information API.
 *
 * Suporte:
 *  - Chrome/Edge Android + desktop: ✅ completo
 *  - Safari iOS / macOS: ❌ não implementa a API. Caímos no fallback "default high"
 *  - Firefox: ❌ idem
 *
 * Pra Safari iOS, a única coisa que dá pra detectar de verdade é online/offline.
 * Por isso `isLowBandwidth` é "best effort" — assume rede boa quando não dá pra saber.
 */
export interface NetworkStatus {
  /** Tem conexão de qualquer tipo? Reflete `navigator.onLine`. */
  online: boolean;
  /** Estimativa do navegador: 'slow-2g' | '2g' | '3g' | '4g' | undefined */
  effectiveType?: string;
  /** Usuário ligou modo de economia de dados (Android Chrome, ou Lite mode). */
  saveData?: boolean;
  /** Bandwidth estimada em Mbps. undefined se não disponível. */
  downlink?: number;
  /**
   * Sinaliza que o app deveria estar em "modo leve" pelo critério de rede.
   * True quando: offline, saveData, 2g/slow-2g, OU downlink < 0.5 Mbps.
   */
  isLowBandwidth: boolean;
}

interface NetworkInformation extends EventTarget {
  readonly effectiveType?: string;
  readonly saveData?: boolean;
  readonly downlink?: number;
  readonly type?: string;
}

interface NavigatorWithConnection extends Navigator {
  connection?: NetworkInformation;
  // Prefixos legacy (Firefox/Safari antigos não têm, mas defensivo)
  mozConnection?: NetworkInformation;
  webkitConnection?: NetworkInformation;
}

function getConnection(): NetworkInformation | undefined {
  if (typeof navigator === 'undefined') return undefined;
  const nav = navigator as NavigatorWithConnection;
  return nav.connection || nav.mozConnection || nav.webkitConnection;
}

function computeStatus(): NetworkStatus {
  if (typeof navigator === 'undefined') {
    return { online: true, isLowBandwidth: false };
  }
  const online = navigator.onLine;
  const conn = getConnection();

  if (!conn) {
    // Safari iOS / Firefox cai aqui — só sabemos online/offline.
    return { online, isLowBandwidth: !online };
  }

  const { effectiveType, saveData, downlink } = conn;
  const slowTypes = new Set(['slow-2g', '2g']);
  const isSlowConnection =
    Boolean(saveData) ||
    (effectiveType !== undefined && slowTypes.has(effectiveType)) ||
    (typeof downlink === 'number' && downlink < 0.5);

  return {
    online,
    effectiveType,
    saveData,
    downlink,
    isLowBandwidth: !online || isSlowConnection,
  };
}

/**
 * Hook que retorna o estado atual da rede, atualizado em eventos `online`,
 * `offline` e `connectionchange`. Re-renderiza apenas quando muda.
 */
export function useNetworkStatus(): NetworkStatus {
  const [status, setStatus] = useState<NetworkStatus>(() => computeStatus());

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const update = () => setStatus(computeStatus());
    window.addEventListener('online', update);
    window.addEventListener('offline', update);

    const conn = getConnection();
    conn?.addEventListener?.('change', update);

    return () => {
      window.removeEventListener('online', update);
      window.removeEventListener('offline', update);
      conn?.removeEventListener?.('change', update);
    };
  }, []);

  return status;
}
